// Admin-only console for managing in-app broadcasts.
// Works on desktop + Android (gated by useIsAdmin like AdminCosts).
import { useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { ArrowLeft, Loader2, Megaphone, Trash2, StopCircle } from "lucide-react";
import { useIsAdmin } from "@/hooks/useIsAdmin";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { formatDistanceToNow } from "date-fns";

interface Broadcast {
  id: string;
  title: string;
  body: string;
  cta_label: string | null;
  cta_url: string | null;
  audience: string;
  starts_at: string;
  ends_at: string | null;
  created_at: string;
}

const AUDIENCES = [
  { value: "all", label: "Everyone" },
  { value: "free", label: "Free users" },
  { value: "paid", label: "Paid users" },
  { value: "android", label: "Android app users" },
];

export default function AdminBroadcasts() {
  const isAdmin = useIsAdmin();
  const { user } = useAuth();
  const { toast } = useToast();

  const [items, setItems] = useState<Broadcast[]>([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [audience, setAudience] = useState("all");
  const [ctaLabel, setCtaLabel] = useState("");
  const [ctaUrl, setCtaUrl] = useState("");
  const [endsAt, setEndsAt] = useState("");

  const load = async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from("broadcasts")
      .select("*")
      .order("created_at", { ascending: false })
      .limit(100);
    if (error) toast({ title: "Couldn't load", description: error.message, variant: "destructive" });
    setItems((data ?? []) as Broadcast[]);
    setLoading(false);
  };

  useEffect(() => { if (isAdmin) void load(); }, [isAdmin]);

  if (isAdmin === null) {
    return <div className="flex h-screen items-center justify-center"><Loader2 className="h-6 w-6 animate-spin" /></div>;
  }
  if (!isAdmin) return <Navigate to="/app/profile" replace />;

  const handleCreate = async () => {
    if (!title.trim() || !body.trim() || !user) {
      toast({ title: "Title and body are required", variant: "destructive" });
      return;
    }
    setSubmitting(true);
    const { error } = await supabase.from("broadcasts").insert({
      title: title.trim(),
      body: body.trim(),
      cta_label: ctaLabel.trim() || null,
      cta_url: ctaUrl.trim() || null,
      audience,
      ends_at: endsAt ? new Date(endsAt).toISOString() : null,
      created_by: user.id,
    });
    setSubmitting(false);
    if (error) {
      toast({ title: "Couldn't publish", description: error.message, variant: "destructive" });
      return;
    }
    toast({ title: "Broadcast published 📣" });
    setTitle(""); setBody(""); setCtaLabel(""); setCtaUrl(""); setEndsAt(""); setAudience("all");
    void load();
  };

  const handleEnd = async (id: string) => {
    const { error } = await supabase
      .from("broadcasts").update({ ends_at: new Date().toISOString() }).eq("id", id);
    if (error) toast({ title: "Couldn't end", description: error.message, variant: "destructive" });
    else { toast({ title: "Broadcast ended" }); void load(); }
  };

  const handleDelete = async (id: string) => {
    if (!confirm("Delete this broadcast permanently?")) return;
    const { error } = await supabase.from("broadcasts").delete().eq("id", id);
    if (error) toast({ title: "Couldn't delete", description: error.message, variant: "destructive" });
    else { toast({ title: "Deleted" }); void load(); }
  };

  const isActive = (b: Broadcast) =>
    new Date(b.starts_at) <= new Date() && (!b.ends_at || new Date(b.ends_at) > new Date());

  return (
    <div className="mx-auto max-w-3xl px-4 py-6">
      <div className="mb-6 flex items-center gap-3">
        <Button asChild variant="ghost" size="sm"><Link to="/app/profile"><ArrowLeft className="mr-1 h-4 w-4" />Back</Link></Button>
        <div>
          <h1 className="flex items-center gap-2 text-xl font-bold"><Megaphone className="h-5 w-5 text-primary" />Broadcasts</h1>
          <p className="text-xs text-muted-foreground">Send in-app announcements to your users.</p>
        </div>
      </div>

      <Card className="mb-6">
        <CardHeader><CardTitle className="text-base">New broadcast</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          <div>
            <Label htmlFor="bc-title">Title *</Label>
            <Input id="bc-title" value={title} onChange={(e) => setTitle(e.target.value)} maxLength={120} placeholder="e.g. 50% off all credits this weekend" />
          </div>
          <div>
            <Label htmlFor="bc-body">Body *</Label>
            <Textarea id="bc-body" value={body} onChange={(e) => setBody(e.target.value)} rows={3} maxLength={500} placeholder="Short message to display." />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label htmlFor="bc-audience">Audience</Label>
              <Select value={audience} onValueChange={setAudience}>
                <SelectTrigger id="bc-audience"><SelectValue /></SelectTrigger>
                <SelectContent>{AUDIENCES.map((a) => (<SelectItem key={a.value} value={a.value}>{a.label}</SelectItem>))}</SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="bc-ends">Ends at (optional)</Label>
              <Input id="bc-ends" type="datetime-local" value={endsAt} onChange={(e) => setEndsAt(e.target.value)} />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label htmlFor="bc-cta">CTA label (optional)</Label>
              <Input id="bc-cta" value={ctaLabel} onChange={(e) => setCtaLabel(e.target.value)} maxLength={32} placeholder="Buy credits" />
            </div>
            <div>
              <Label htmlFor="bc-url">CTA URL (optional)</Label>
              <Input id="bc-url" value={ctaUrl} onChange={(e) => setCtaUrl(e.target.value)} placeholder="/app or https://..." />
            </div>
          </div>
          <Button onClick={handleCreate} disabled={submitting} className="w-full">
            {submitting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Megaphone className="mr-2 h-4 w-4" />}
            Publish broadcast
          </Button>
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle className="text-base">All broadcasts</CardTitle></CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-8"><Loader2 className="h-5 w-5 animate-spin" /></div>
          ) : items.length === 0 ? (
            <p className="py-6 text-center text-sm text-muted-foreground">No broadcasts yet.</p>
          ) : (
            <div className="space-y-3">
              {items.map((b) => (
                <div key={b.id} className="rounded-lg border border-white/10 bg-card/50 p-3">
                  <div className="mb-1 flex items-start justify-between gap-2">
                    <div className="min-w-0 flex-1">
                      <p className="truncate font-semibold">{b.title}</p>
                      <p className="line-clamp-2 text-xs text-muted-foreground">{b.body}</p>
                    </div>
                    <span className={`shrink-0 rounded-full px-2 py-0.5 text-[10px] font-semibold ${isActive(b) ? "bg-emerald-500/15 text-emerald-500" : "bg-muted text-muted-foreground"}`}>
                      {isActive(b) ? "ACTIVE" : "ENDED"}
                    </span>
                  </div>
                  <div className="flex items-center justify-between gap-2 text-[11px] text-muted-foreground">
                    <span>{AUDIENCES.find((a) => a.value === b.audience)?.label ?? b.audience} · {formatDistanceToNow(new Date(b.created_at), { addSuffix: true })}</span>
                    <div className="flex gap-1">
                      {isActive(b) && (
                        <Button size="sm" variant="ghost" onClick={() => void handleEnd(b.id)}>
                          <StopCircle className="mr-1 h-3 w-3" />End now
                        </Button>
                      )}
                      <Button size="sm" variant="ghost" onClick={() => void handleDelete(b.id)}>
                        <Trash2 className="h-3 w-3 text-destructive" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
